import { Typography } from "@mui/material";

import MuiTextField, { TextFieldProps } from "@mui/material/TextField";
import sanitizeHtml from "sanitize-html";
import { FieldProps } from "./FieldProps";
import { InputTextField } from "./InputTextField";
import { RawEditor, HtmlEditor, Expandable } from "@rich-editor/core";
import * as api from "@crochik/pi-api";
import Section from "./Section";
import { useCallback, useState } from "react";
import IconPicker from "../IconPicker";
interface IRowProperties extends FieldProps<string> {
  children?: React.ReactElement;
}

function Row(props: IRowProperties) {
  const { field, children, disabled } = props;
  // const theme = useTheme(); // how to get the background color for disabled fields from theme? ?!??!?!?!?!
  // ...
  const backgroundColor = disabled ? "#e0e0e0" : "#fcfcfc";
  return (
    <div
      key={field.name}
      style={{
        marginTop: 6,
        marginBottom: 6,
        backgroundColor,
        padding: 8,
        borderRadius: 6,
      }}
    >
      <Typography variant="subtitle2" color="default">
        {field.label || field.name}
      </Typography>
      {children}
    </div>
  );
}

export function HtmlField(props: FieldProps<string>) {
  const { value } = props;

  const clean = value
    ? sanitizeHtml(value, {
        allowedTags: ["p", "em", "strong", "b", "i", "span"],
        // allowedAttributes: {
        //     'p': ["style"],
        // },
        allowedStyles: {
          "*": {
            // Match HEX and RGB
            color: [
              /^#(0x)?[0-9a-f]+$/i,
              /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
            ],
            "text-align": [/^left$/, /^right$/, /^center$/],
            // Match any number with px, em, or %
            // 'font-size': [/^\d+(?:px|em|%)$/]
          },
          // 'p': {
          //     'font-size': [/^\d+rem$/]
          // }
        },
      })
    : "<div>&nbsp;</div>";

  return (
    <Row {...props}>
      <div
        style={{ maxHeight: "300px", overflow: "auto", padding: 8 }}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </Row>
  );
}

export class TextField extends InputTextField {
  render() {
    const options = this.props.field.options as api.TextFieldOptions;

    const { multline, contentType } = options ?? {};
    if (this.props.disabled && contentType === "text/html") {
      const { field } = this.props;
      return (
        <Section
          title={field.label ?? field.name ?? ""}
          style={{ paddingLeft: 0 }}
        >
          <HtmlField {...this.props} />
        </Section>
      );
    }

    var props: TextFieldProps = {
      ...this.calcProps(),
      fullWidth: true,
      type: "text",
      sx: {
        marginTop: "16px",
        marginBottom: "8px",
        maxHeight: "300px",
        overflow: "auto",
      },
    };

    if (multline) {
      // prevent auto-submit and allow multiple lines
      props = {
        ...props,
        multiline: true,
      };

      if (Array.isArray(this.props.value)) {
        props.value = this.props.value.reduce((p, c) =>
          !!p ? `${p}\n${c}` : c,
        );
      }

      // TODO: add explicit optin (or just for some content types)
      // ...
      if (options.contentType != null && options.contentType != "text/plain") {
        return <ContentEditor fieldProps={this.props} options={options} />;
      }

      delete props.onKeyPress;

      return <MuiTextField {...props} variant="filled" className="TextField" />;
    }

    // TODO: hack warning!
    // should use some option value to decide
    // ...
    if (this.props.field.name == "Icon" || this.props.field.label == "Icon") {
      const { field } = this.props;
      const label = field.label ?? field.name!;
      const onChange = (name) => {
        this.props.onChange?.(field, name);
      };
      return (
        <IconPicker
          disabled={props.disabled}
          onChange={onChange}
          value={(props.value as string) ?? ""}
          label={label}
          sx={props.sx}
        />
      );
    }

    return <MuiTextField {...props} variant="filled" className="TextField" />;
  }
}

function ContentEditor({
  options,
  fieldProps,
}: {
  options: api.TextFieldOptions;
  fieldProps: FieldProps<string>;
}) {
  const { field, onChange } = fieldProps;
  const [expanded, setExpanded] = useState(false);

  const expand = () => setExpanded(true);
  const { allowExpressions } = options;

  const onValueChanged = useCallback(
    (value) => {
      if (onChange) onChange(field, value);
    },
    [onChange, field],
  );

  if (options.contentType == "text/html") {
    return (
      <Section
        title={field.label ?? field.name ?? ""}
        style={{ paddingLeft: 0 }}
        onFullScreen={expand}
      >
        <Expandable
          expandable={true}
          isExpanded={expanded}
          onToggle={setExpanded}
        >
          <HtmlEditor
            maxHeight={expanded ? undefined : "500"}
            initialContent={fieldProps.value ?? ""}
            allowFreeformHandlebars={allowExpressions}
            onChange={onValueChanged}
          />
        </Expandable>
      </Section>
    );
  }

  // TODO: calculate language from contentType
  // ...

  return (
    <Section
      title={field.label ?? field.name ?? ""}
      style={{ paddingLeft: 0 }}
      onFullScreen={expand}
    >
      <Expandable
        expandable={true}
        isExpanded={expanded}
        onToggle={setExpanded}
      >
        <RawEditor
          maxHeight={expanded ? undefined : "500"}
          value={fieldProps.value ?? ""}
          onChange={onValueChanged}
          allowFreeformHandlebars={allowExpressions}
          // language={rawLanguage}
          // placeholder={`Enter ${rawLanguage} content...`}
          // autocompleteProvider={autocompleteProvider}
          // indentStyle={rawIndentStyle}
        />
      </Expandable>
    </Section>
  );
}
